// /resources/js/app/modules/v2/V2LiveUpdater.js
import axios from 'axios';
import { v2Renderer } from './V2Renderer.js';

/**
 * @module V2LiveUpdater
 * This module is responsible for "cosmetic" real-time updates on visible,
 * unlocked signals. It fetches live data and delegates rendering updates.
 * It uses a visibility-aware "smart polling" mechanism to conserve resources.
 */
class V2LiveUpdater {
    constructor() {
        this.mainController = null;
        this.pollTimeoutId = null; // Holds the ID for our setTimeout, allowing it to be cleared.
        this.previousDataCache = new Map(); // Cache to detect changes for event badges
        this.BATCH_SIZE = 30; // DexScreener's recommended max addresses per request

        // --- NEW: CONFIGURATION & STATE FOR SMART POLLING ---
        this.POLL_INTERVAL_MS = 60000; // Poll every 60 seconds.
        this.lastSuccessfulFetchTimestamp = 0; // Tracks the last successful API call to enable smart refreshing.
    }

    /**
     * Initializes the updater with a reference to the main controller and
     * sets up the visibility change listener.
     * @param {V2MainController} controller - The main application controller instance.
     */
    init(controller) {
        this.mainController = controller;
        // Listen for when the browser tab's visibility changes.
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    /**
     * Starts the polling mechanism.
     * It now simply kicks off the first cycle if the tab is visible.
     * The cycle itself is responsible for scheduling subsequent runs.
     */
    startPolling() {
        this.stopPolling(); // Ensure no duplicate timers are running.
        if (!document.hidden) {
            this.runUpdateCycle();
        }
    }

    /**
     * Stops the polling mechanism by clearing any scheduled timeout.
     */
    stopPolling() {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
    }

    /**
     * --- NEW ---
     * This is the core of the smart polling logic. It activates and deactivates
     * polling based on whether the user is actively viewing the tab.
     */
    handleVisibilityChange() {
        if (document.hidden) {
            console.log('[V2LiveUpdater] Tab is hidden. Stopping live updates.');
            this.stopPolling();
        } else {
            console.log('[V2LiveUpdater] Tab is visible again. Checking if data is stale...');
            const timeSinceLastFetch = Date.now() - this.lastSuccessfulFetchTimestamp;

            if (timeSinceLastFetch >= this.POLL_INTERVAL_MS) {
                console.log(`[V2LiveUpdater] Data is stale (${Math.round(timeSinceLastFetch / 1000)}s old). Fetching immediately.`);
                this.runUpdateCycle();
            } else {
                const timeUntilNextFetch = this.POLL_INTERVAL_MS - timeSinceLastFetch;
                console.log(`[V2LiveUpdater] Data is fresh. Scheduling next update in ${Math.round(timeUntilNextFetch / 1000)}s.`);
                this.pollTimeoutId = setTimeout(() => this.runUpdateCycle(), timeUntilNextFetch);
            }
        }
    }


    /**
     * Executes one full update cycle and schedules the next one.
     */
    async runUpdateCycle() {
        // Clear any existing timer to prevent race conditions.
        this.stopPolling();

        if (!this.mainController) return;

        const signalsToUpdate = this.mainController.getVisibleUnlockedSignals();
        // If there's nothing to update, just schedule the next check and exit.
        if (signalsToUpdate.length === 0) {
            if (!document.hidden) {
                this.pollTimeoutId = setTimeout(() => this.runUpdateCycle(), this.POLL_INTERVAL_MS);
            }
            return;
        }

        console.log(`[V2LiveUpdater] Starting update cycle for ${signalsToUpdate.length} visible signals.`);

        try {
            // Group addresses by chain for efficient batching
            const assetsByChain = signalsToUpdate.reduce((acc, signal) => {
                const asset = signal.asset_info || signal.asset;
                //const chain = signal.asset.chain_id;
                if (!acc[chain]) acc[chain] = new Set();
                //acc[chain].add(signal.asset.token_address);
                acc[chain].add(asset.token_address);

                return acc;
            }, {});

            const fetchPromises = [];
            
            for (const [chain, addressesSet] of Object.entries(assetsByChain)) {
                const addresses = [...addressesSet];
                console.log(`[V2LiveUpdater] Processing ${addresses.length} unique addresses for chain: ${chain}`);

                for (let i = 0; i < addresses.length; i += this.BATCH_SIZE) {
                    const addressChunk = addresses.slice(i, i + this.BATCH_SIZE);
                    console.log(`[V2LiveUpdater]   - Fetching batch ${Math.floor(i / this.BATCH_SIZE) + 1} for ${chain} (${addressChunk.length} addresses)`);
                    const url = `https://api.dexscreener.com/tokens/v1/${chain}/${addressChunk.join(',')}`;
                    fetchPromises.push(axios.get(url));
                }
            }

            const responses = await Promise.all(fetchPromises);
            const liveDataMap = new Map();
            responses.forEach(response => {
                response.data?.forEach(pair => {
                    const identifier = `${pair.chainId}:${pair.baseToken.address.toLowerCase()}`;
                    liveDataMap.set(identifier, pair);
                });
            });

            // Iterate through signals and update DOM if new data is available

            signalsToUpdate.forEach(consolidatedSignal => {
                const asset = consolidatedSignal.asset_info;
                const identifier = `${asset.chain_id}:${asset.token_address.toLowerCase()}`;
                const liveApiData = liveDataMap.get(identifier);

                if (liveApiData) {
                    // Call the renderer's update method, passing the necessary data
                    v2Renderer.updateSignalDOM(consolidatedSignal, liveApiData);
                    this.previousDataCache.set(asset.id, liveApiData); // Cache the new API data
                }
            });
            
            // --- NEW: UPDATE TIMESTAMP ON SUCCESS ---
            this.lastSuccessfulFetchTimestamp = Date.now();
            console.log(`[V2LiveUpdater] Update cycle finished successfully. Found live data for ${liveDataMap.size} signals.`);

        } catch (error) {
            console.warn('[V2LiveUpdater] Failed to fetch live data:', error.message);
        } finally {
            // --- NEW: SCHEDULE THE NEXT RUN ---
            // Always schedule the next run, even on failure, to allow for recovery.
            // Only schedule if the tab is still visible.
            if (!document.hidden) {
                this.pollTimeoutId = setTimeout(() => this.runUpdateCycle(), this.POLL_INTERVAL_MS);
            }
        }
    }
}

export const v2LiveUpdater = new V2LiveUpdater();